import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Radio } from "lucide-react";
import FollowHostButton from "@/components/FollowHostButton";

const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function initials(name) {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export default function Hosts() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/hosts");
        setHosts(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="hosts-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
      <div className="label-eyebrow mb-6 text-[var(--ff-red)]">▮ NETWORK / HOSTS</div>
      <h1 className="font-display text-5xl lg:text-8xl mb-4 max-w-5xl">MEET THE HOSTS.</h1>
      <p className="text-[var(--ff-text-dim)] text-lg max-w-3xl mb-16">
        The voices, faces, and personalities behind every show on the Freshy Fresh network.
      </p>

      {loading ? (
        <div className="text-[var(--ff-text-dim)]">Loading hosts...</div>
      ) : hosts.length === 0 ? (
        <div className="surface-card p-12 text-center text-[var(--ff-text-dim)]">
          No hosts on the roster yet. Site admin can add hosts from the dashboard.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ff-border)]">
          {hosts.map((h) => (
            <Link
              key={h.name}
              to={`/hosts/${encodeURIComponent(h.name)}`}
              className="bg-[var(--ff-surface)] p-8 hover:bg-[var(--ff-surface-hover)] transition-colors group"
              data-testid={`host-card-${h.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="flex items-start gap-4 mb-6">
                {h.avatar_url ? (
                  <img src={h.avatar_url} alt={h.name} className="w-16 h-16 object-cover" />
                ) : (
                  <div className="w-16 h-16 bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-xl">
                    {initials(h.name)}
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-display text-2xl leading-tight">{h.name}</div>
                  {h.title && <div className="label-eyebrow mt-1 text-[var(--ff-text-dim)]">{h.title}</div>}
                </div>
              </div>
              {h.bio && (
                <p className="text-sm text-[var(--ff-text-dim)] leading-relaxed line-clamp-3 mb-4">{h.bio}</p>
              )}
              <div className="mt-6 pt-4 border-t border-[var(--ff-border)] flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-mono-ui text-[var(--ff-text-dim)]">
                  <Radio size={12} /> {h.shows.length} SHOW{h.shows.length === 1 ? "" : "S"}
                </div>
                <div className="flex items-center gap-3" onClick={(e) => e.preventDefault()}>
                  <FollowHostButton hostName={h.name} size="sm" />
                  <ArrowRight size={16} className="text-[var(--ff-red)] group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
              {h.shows.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {h.shows.slice(0, 3).map((s) => (
                    <span key={s.id} className="font-mono-ui text-[0.65rem] uppercase tracking-[0.12em] px-2 py-1 border border-[var(--ff-border)] text-[var(--ff-text-dim)]">
                      {s.title}
                    </span>
                  ))}
                  {h.shows.length > 3 && (
                    <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.12em] px-2 py-1 text-[var(--ff-text-dim)]">
                      +{h.shows.length - 3} MORE
                    </span>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
